<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap gap-2" role="toolbar" :aria-label="$t('contractAddition.formatting')">
      <button v-for="action in actions" :key="action.key" type="button" class="btn ghost small"
        :disabled="disabled || sourceMode" :aria-pressed="editor?.isActive(action.active) || false" @click="action.run()">
        {{ $t(`contractAddition.${action.key}`) }}
      </button>
      <button type="button" class="btn ghost small" :disabled="disabled" :aria-pressed="sourceMode" @click="sourceMode = !sourceMode">
        {{ $t('contractAddition.htmlSource') }}
      </button>
    </div>
    <textarea v-if="sourceMode" :value="modelValue" :disabled="disabled" class="w-full min-h-48 font-mono"
      :aria-label="$t('contractAddition.htmlSource')" @input="$emit('update:modelValue', $event.target.value)" />
    <EditorContent v-else :editor="editor" />
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'

const props = defineProps({ modelValue: { type: String, default: '' }, label: { type: String, required: true }, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const sourceMode = ref(false)
const editor = useEditor({
  content: props.modelValue,
  editable: !props.disabled,
  extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, link: false, code: false, codeBlock: false, horizontalRule: false })],
  editorProps: { attributes: { class: 'min-h-48 p-3 border border-[var(--color-border)] rounded-xl outline-none [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-bold [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6', 'aria-label': props.label } },
  onUpdate: ({ editor: current }) => emit('update:modelValue', current.getHTML()),
})
const actions = [
  { key: 'bold', active: 'bold', run: () => editor.value?.chain().focus().toggleBold().run() },
  { key: 'italic', active: 'italic', run: () => editor.value?.chain().focus().toggleItalic().run() },
  { key: 'heading', active: 'heading', run: () => editor.value?.chain().focus().toggleHeading({ level: 2 }).run() },
  { key: 'bulletList', active: 'bulletList', run: () => editor.value?.chain().focus().toggleBulletList().run() },
  { key: 'orderedList', active: 'orderedList', run: () => editor.value?.chain().focus().toggleOrderedList().run() },
]
watch(() => props.modelValue, value => {
  if (editor.value && editor.value.getHTML() !== value) editor.value.commands.setContent(value, { emitUpdate: false })
})
watch(() => props.disabled, value => editor.value?.setEditable(!value))
onBeforeUnmount(() => editor.value?.destroy())
</script>
